import { Box, Button, ListItemAvatar } from "@mui/material";
import { useCreateComment } from "services/forum";
import React, {
  forwardRef,
  useContext,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { ForumComment } from "protogen/forum_service_pb";
import ListItem from "@mui/material/ListItem";
import { UserAvatar } from "../common/CurrentUserAvatar";
import { CurrentUserContext } from "../context/RequireAuth";
import { RichContent } from "../editor/utils";
import SimpleRichEditor, { Handle } from "../editor/SimpleRichEditor";
import useCached from "../hooks/useCached";
import { JSONContent } from "@tiptap/react";

type Props = {
  postRef: string;
  parentRef?: string | null;
  onCreate: (c: ForumComment) => void;
};

export type CommentHandle = {
  focus: () => void;
};

export default forwardRef<CommentHandle, Props>(
  ({ postRef, parentRef = null, onCreate }: Props, ref) => {
    const editorRef = useRef<Handle>(null);
    const currentUser = useContext(CurrentUserContext);
    const [content, setContent] = useCached<RichContent | null>(
      `forum-comment-${postRef}-${parentRef}`,
      null,
    );
    const _initialContent: string | JSONContent | undefined =
      (content?.text?.length && content?.json && JSON.parse(content?.json)) ||
      undefined;
    const [commentKey, setCommentKey] = useState("");
    const [uploadsInProgress, setUploadsInProgress] = useState(false);
    const { request, loading } = useCreateComment();
    useImperativeHandle(ref, () => ({
      focus: () => {
        editorRef?.current?.focus();
      },
    }));

    const handleComment = async () => {
      if (!content) return;
      const resp = await request({
        postRef,
        parentRef: parentRef || "",
        body: {
          contentType: "json",
          payload: content.json,
          textContent: content.text,
          attachments: content.attachments,
        },
      });
      if (!resp?.comment) return;
      setContent(null);
      onCreate(resp.comment);
      setCommentKey(resp.comment.ref);
    };
    return (
      <ListItem
        sx={{
          marginTop: "16px",
          marginBottom: "16px",
          padding: "8px 0",
          width: "100%",
          minWidth: 0,
          overflow: "hidden",
        }}
      >
        <ListItemAvatar sx={{ minWidth: "48px", flexShrink: 0 }}>
          <UserAvatar user={currentUser} size={40} />
        </ListItemAvatar>
        <Box sx={{ flex: 1, minWidth: 0, overflow: "hidden" }}>
          <SimpleRichEditor
            ref={editorRef}
            key={commentKey}
            disabled={loading}
            setContent={setContent}
            placeholder="Reply..."
            setUploadsInProgress={setUploadsInProgress}
            initialContent={_initialContent}
            primaryAction={
              <Button
                size="small"
                sx={{
                  height: "unset",
                  padding: "8px 18px",
                }}
                onClick={handleComment}
                disabled={loading || !content || uploadsInProgress}
              >
                Reply
              </Button>
            }
          />
        </Box>
      </ListItem>
    );
  },
);
